import { getDomain, getHostname } from 'tldts'
import AnimatedLoaderDupeLogo from '~/src/components/AnimatedLoaderDupeLogo'
import CyclePhrases from '~/src/components/CyclePhrases'
import useGetUrlFromHash from '~/src/hooks/useGetUrlFromHash'
import { DEFAULT_LOADING_COPY } from '~/src/lib/constants'
import { cn } from '~/src/lib/utils'

const DEFAULT_LOADED_MESSAGE = "Let's see those results!"

interface Props extends React.HTMLProps<HTMLDivElement> {
  customCopy?: string[]
  hideDomain?: boolean
  autoplay?: boolean
  loop?: boolean
  loaded?: boolean
  loadedMessage?: string
  continuous?: boolean
  forceTheme?: 'light' | 'dark'
  width?: number | string
  height?: number | string
  hideLogo?: boolean
}

const PageLoader: React.FC<Props> = ({
  customCopy,
  hideDomain,
  autoplay,
  loop,
  loaded,
  loadedMessage,
  continuous,
  className,
  forceTheme,
  width = 342,
  height = 200,
  hideLogo,
  ...props
}) => {
  const LOADING_COPY = customCopy || DEFAULT_LOADING_COPY
  const LOADED_MESSAGE = loadedMessage || DEFAULT_LOADED_MESSAGE

  const isImageKitUrl = (url: string | undefined) =>
    (url || '').includes('ik.imagekit.io')

  const { url } = useGetUrlFromHash()
  const domain = (() => {
    if (isImageKitUrl(url) || !url) return undefined
    const hostname = getHostname(url)
    if (hostname?.includes('bridge.ric1.admarketplace.net')) {
      return 'amazon.com'
    }
    return getDomain(url) ?? undefined
  })()

  return (
    <div
      data-testid='page-loader'
      className={cn('relative max-w-full overflow-hidden', className)}
      {...props}
    >
      {!hideLogo && (
        <AnimatedLoaderDupeLogo
          autoplay={autoplay}
          loop={loop}
          forceTheme={forceTheme}
          width={width}
          height={height}
        />
      )}

      <div
        className='text-center'
        style={hideLogo ? {} : { marginTop: Number(height) * -0.3 }}
      >
        <div className='font-cta text-md mx-auto max-w-sm text-center text-sm uppercase md:text-base'>
          {loaded ? (
            <span>{LOADED_MESSAGE}</span>
          ) : (
            <CyclePhrases
              phrases={LOADING_COPY}
              continuous={!loaded && continuous}
            />
          )}
        </div>
        <div
          className={cn('text-sm transition-opacity md:text-base', {
            'opacity-30': domain && !hideDomain,
            'opacity-0': !domain || hideDomain,
          })}
        >
          <div className='mx-auto max-w-xs truncate'>
            Looking up item from <span>{domain}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PageLoader
